import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  ModalProps,
  Radio,
  Row,
  Select
} from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'

import {
  useClassifierServiceGetInvestigationClassifierEndpoint,
  useClassifierServiceGetInvestigationClassifierListEndpoint,
  useInvestigationsServiceCreateExternalInvestigationResultEndpoint,
  useInvestigationsServiceGetInvestigationTestsEndpointKey,
  useInvestigationsServiceGetListSpecimensEndpointKey,
  useInvestigationsServiceGetSpecimenEndpointKey,
  usePersonsServiceGetPersonByIdEndpoint
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  CreateExternalInvestigationResultRequest
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  ClassifierInfiniteSelect,
  CustomItemRender,
  InternalInstitutionsSelect,
  message,
  pickReferralDoctor,
  ReferralDoctorFields,
  SaveConfirm,
  Text,
  Upload
} from 'src/components'
import { getFullName } from 'src/features/users/utils'
import { useClassifierId, useFileHandler } from 'src/hooks'
import {
  disabledFutureDate,
  dayMonthYearHourMinutesFormatWithDot
} from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { SearchPersonModal } from '../SearchPersonModal'

import { ExternalResultReceptionFields } from './ExternalResultReceptionFields'
import { useExternalResultReception } from './useExternalResultReception'

type SourceType = 'registered' | 'freeText'

type Props = {
  onCancel: () => void
  onSuccess?: () => void
  /** When provided the modal is person-scoped; otherwise a patient selector is shown. */
  personId?: string
} & ModalProps

export const ExternalResultModal = ({
  onCancel,
  onSuccess,
  personId,
  ...props
}: Props) => {
  const { t, currentLang } = useLocales()
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const lang = currentLang.value

  const isGlobal = !personId
  const [selectedPersonId, setSelectedPersonId] = useState<string>()
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [sourceType, setSourceType] = useState<SourceType>('registered')
  const [isSaving, setIsSaving] = useState(false)

  const effectivePersonId = personId ?? selectedPersonId

  const { onFileChange, saveFiles, isUploading } = useFileHandler({
    form,
    folder: 'results'
  })

  const { data: selectedPerson } = usePersonsServiceGetPersonByIdEndpoint(
    { id: String(selectedPersonId) },
    [selectedPersonId],
    { enabled: !!selectedPersonId }
  )

  const simeCodeSuffix = selectedPerson?.simeCode
    ? ` (${selectedPerson.simeCode})`
    : ''
  const patientLabel = selectedPerson
    ? `${getFullName(selectedPerson.firstName, selectedPerson.lastName)}${simeCodeSuffix}`
    : ''

  const categoryClassifierId = useClassifierId(
    ClassifierType.INVESTIGATION_CATEGORY,
    ClassifierCategory.INVESTIGATIONS
  )

  const categoryId = Form.useWatch('categoryId', form)
  const investigationTypeId = Form.useWatch('investigationTypeId', form)

  const { data: investigationTypes } =
    useClassifierServiceGetInvestigationClassifierListEndpoint(
      { pageNumber: 1, pageSize: 100, categoryIds: [String(categoryId)] },
      [categoryId],
      { enabled: !!categoryId }
    )

  const { data: investigationType } =
    useClassifierServiceGetInvestigationClassifierEndpoint(
      { id: String(investigationTypeId) },
      [investigationTypeId],
      { enabled: !!investigationTypeId }
    )

  const testTypes = investigationType?.testTypes ?? []
  const specimenTypes = investigationType?.specimenTypes ?? []

  const { captureLatestSpecimenId, resolveCreatedSpecimenId, applyReception } =
    useExternalResultReception()

  const { mutateAsync: createExternalResult } =
    useInvestigationsServiceCreateExternalInvestigationResultEndpoint()

  const invalidate = () =>
    [
      useInvestigationsServiceGetListSpecimensEndpointKey,
      useInvestigationsServiceGetInvestigationTestsEndpointKey,
      useInvestigationsServiceGetSpecimenEndpointKey
    ].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))

  const onSelectPerson = (id: string) => {
    setSelectedPersonId(id)
    setIsSearchOpen(false)
  }

  // Reset the dependent selections whenever an upstream classifier changes so a
  // stale test type can't be submitted against a new category.
  const onCategoryChange = () =>
    form.resetFields(['investigationTypeId', 'specimenTypeId', 'testTypeId'])

  const onInvestigationTypeChange = () =>
    form.resetFields(['specimenTypeId', 'testTypeId'])

  const onSourceTypeChange = (value: SourceType) => {
    setSourceType(value)
    form.resetFields(['sourceLaboratoryId', 'sourceLaboratoryName'])
  }

  const onSubmit = async () => {
    const values = await form.validateFields()

    if (!effectivePersonId) {
      return message.error(t('persons.external_result.select_patient_error'))
    }

    setIsSaving(true)

    try {
      const mediaItemIds = await saveFiles()

      const payload: Omit<
        CreateExternalInvestigationResultRequest,
        'resultOptionId' | 'resultValueOptionId' | 'resultDate'
      > = {
        personId: effectivePersonId,
        categoryId: values.categoryId,
        specimenTypeId: values.specimenTypeId || undefined,
        testTypeId: values.testTypeId,
        sampleCollectedAt: dayjs(values.sampleCollectedAt).format(),
        sourceLaboratoryId:
          sourceType === 'registered'
            ? values.sourceLaboratoryId || undefined
            : undefined,
        sourceLaboratoryName:
          sourceType === 'freeText'
            ? values.sourceLaboratoryName || undefined
            : undefined,
        ...pickReferralDoctor(values),
        mediaItemIds: mediaItemIds.length ? mediaItemIds : undefined
      }

      const previousSpecimenId =
        await captureLatestSpecimenId(effectivePersonId)

      await createExternalResult({
        requestBody: payload as CreateExternalInvestigationResultRequest
      })

      message.success(t('investigations.successfully_added_result'))

      // Reception and conformity are not part of the external result contract,
      // so they are written separately on the specimen it generated. The result
      // itself is already saved at this point, hence the warning instead of an
      // error when the specimen cannot be reached.
      const specimenId = await resolveCreatedSpecimenId(
        effectivePersonId,
        previousSpecimenId
      )

      try {
        if (!specimenId) throw new Error('specimen not resolved')
        await applyReception(specimenId, values)
      } catch {
        message.warning(t('persons.external_result.reception_not_saved'))
      }

      invalidate()
      onSuccess?.()
      onCancel()
    } catch (err) {
      formErrorHandler((err as ApiError).body?.errors)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      open
      centered
      width={800}
      footer={null}
      maskClosable={false}
      title={t('persons.external_result.title')}
      onCancel={onCancel}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      {...props}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ receivedAt: dayjs() }}
      >
        {/* Source laboratory comes first: it is what the reception starts from,
            before the patient the sample belongs to. */}
        <Row gutter={[16, 12]}>
          <Col span={24}>
            <Form.Item label={t('persons.external_result.source_laboratory')}>
              <Radio.Group
                value={sourceType}
                onChange={(e) => onSourceTypeChange(e.target.value)}
              >
                <Radio value="registered">
                  {t('persons.external_result.source_registered')}
                </Radio>
                <Radio value="freeText">
                  {t('persons.external_result.source_free_text')}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={24}>
            {sourceType === 'registered' ? (
              <Form.Item name="sourceLaboratoryId">
                <InternalInstitutionsSelect
                  placeholder={t('persons.external_result.source_laboratory')}
                />
              </Form.Item>
            ) : (
              <Form.Item name="sourceLaboratoryName">
                <Input
                  placeholder={t(
                    'persons.external_result.source_laboratory_name'
                  )}
                />
              </Form.Item>
            )}
          </Col>

          <ReferralDoctorFields form={form} />
        </Row>

        <Divider />

        {isGlobal && (
          <>
            <Flex align="end" gap={8}>
              <Form.Item
                className="full-width"
                label={t('persons.external_result.patient')}
                required
              >
                <Input
                  readOnly
                  value={patientLabel}
                  placeholder={t('persons.external_result.select_patient')}
                />
              </Form.Item>

              <Form.Item>
                <Button onClick={() => setIsSearchOpen(true)}>
                  {t('common.search')}
                </Button>
              </Form.Item>
            </Flex>

            <Divider />
          </>
        )}

        <Row gutter={[16, 12]}>
          <Col span={12}>
            <Form.Item
              name="categoryId"
              label={t('classifiers.category')}
              rules={requiredRules}
            >
              <ClassifierInfiniteSelect
                id={categoryClassifierId}
                placeholder={t('classifiers.category')}
                onChange={onCategoryChange}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="investigationTypeId"
              label={t('laboratory_classifiers.investigation_type')}
              rules={requiredRules}
            >
              <Select
                disabled={!categoryId}
                placeholder={t('laboratory_classifiers.investigation_type')}
                options={investigationTypes?.data?.map((item) => ({
                  value: item.id,
                  label: item.title?.[lang]
                }))}
                onChange={onInvestigationTypeChange}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="testTypeId"
              label={t('specimens.test_type')}
              rules={requiredRules}
            >
              <Select
                disabled={!investigationTypeId}
                placeholder={t('specimens.test_type')}
                options={testTypes.map((item) => ({
                  value: item.id,
                  label:
                    item.title?.[lang] ?? item.infection?.translation?.[lang]
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="specimenTypeId"
              label={t('consultations.specimen_type')}
            >
              <Select
                allowClear
                disabled={!investigationTypeId}
                placeholder={t('consultations.specimen_type')}
                options={specimenTypes.map((item) => ({
                  value: item.id,
                  label: item.translation?.[lang]
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="sampleCollectedAt"
              label={t('persons.external_result.sample_collected_at')}
              rules={requiredRules}
            >
              <DatePicker
                className="full-width"
                showTime
                showSecond={false}
                disabledDate={disabledFutureDate}
                format={dayMonthYearHourMinutesFormatWithDot}
              />
            </Form.Item>
          </Col>

          <ExternalResultReceptionFields form={form} />

          <Col span={24}>
            <Text type="secondary" size="sm">
              {t('stock_applications.uploaded_documents')}
            </Text>
            <Form.Item name="docs" className="full-width">
              <Upload
                onChange={onFileChange}
                customRequest={() => null}
                itemRender={(_, file, __, { remove }) => (
                  <CustomItemRender file={file} onRemove={remove} />
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        <Flex justify="center" gap={8}>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>

          <SaveConfirm
            title={`${t('common.confirm')}?`}
            onConfirm={onSubmit}
            okButtonProps={{ loading: isSaving || isUploading }}
          >
            <Button type="primary">{t('common.confirm')}</Button>
          </SaveConfirm>
        </Flex>
      </Form>

      {isSearchOpen && (
        <SearchPersonModal
          selectMode
          onAddClick={onSelectPerson}
          onCancel={() => setIsSearchOpen(false)}
        />
      )}
    </Modal>
  )
}
